/**
 * ChangelogEntry — Single changelog entry detail page
 * Public page for changelog.example.com/:locale/:slug
 */

import { T } from '../styles/tokens';
import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { route } from 'preact-router';
import { Button } from '../components/ui';

const API_BASE = '/api/v1/cms';

interface Entry {
  id: string; slug: string; metadata: string; tags: string; author_name: string;
  published_at: string; created_at: string; title: string; body: string; excerpt: string;
}

interface RecentEntry {
  id: string; slug: string; metadata: string; tags: string; published_at: string; created_at: string; title: string;
}

const TAG_COLORS: Record<string, string> = {
  feature: T.success, fix: T.danger, improvement: T.primary, breaking: T.warning,
};

const TAG_LABELS: Record<string, string> = {
  feature: 'Feature', fix: 'Fix', improvement: 'Melhoria', breaking: 'Breaking',
};

const S = {
  page: { minHeight: '100vh', background: T.bg, color: T.text, fontFamily: "'Inter', -apple-system, sans-serif" } as any,
  container: { maxWidth: 1100, margin: '0 auto', padding: '40px 20px', display: 'flex', gap: 40 } as any,
  main: { flex: 1, minWidth: 0 } as any,
  sidebar: { width: 280, flexShrink: 0 } as any,
  backLink: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: T.primary, textDecoration: 'none', cursor: 'pointer', marginBottom: 24, background: 'none', border: 'none', fontFamily: 'inherit' } as any,
  card: { background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 10, padding: '28px 32px' } as any,
  title: { fontSize: 28, fontWeight: 700, marginBottom: 12, lineHeight: 1.3, letterSpacing: '-0.02em' },
  meta: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' as const },
  date: { fontSize: 14, color: T.textMuted },
  version: { fontSize: 13, color: T.primary, fontWeight: 500, background: T.primaryBg, padding: '2px 10px', borderRadius: 4 },
  tags: { display: 'flex', gap: 6, flexWrap: 'wrap' as const },
  tag: (color: string) => ({ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: `${color}20`, color }),
  content: { fontSize: 15, lineHeight: 1.8, color: '#C0C8D8' } as any,
  sidebarTitle: { fontSize: 14, fontWeight: 600, color: T.textSecondary, marginBottom: 12, textTransform: 'uppercase' as const, letterSpacing: '0.5px' },
  recentCard: { background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 8, padding: '12px 16px', marginBottom: 8, cursor: 'pointer', transition: 'border-color 0.15s' } as any,
  recentTitle: { fontSize: 14, fontWeight: 500, marginBottom: 4, lineHeight: 1.3 },
  recentDate: { fontSize: 12, color: T.textMuted },
  skeleton: { background: T.bgCardHover, borderRadius: 8, height: 300, marginBottom: 16 } as any,
  sidebarSkeleton: { background: T.bgCardHover, borderRadius: 8, height: 60, marginBottom: 8 } as any,
};

function formatDate(d: string): string {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('pt-BR', { year: 'numeric', month: 'long', day: 'numeric' }); } catch { return d; }
}

function parseTags(tags: string): string[] {
  try { return JSON.parse(tags || '[]'); } catch { return []; }
}

/** Simple markdown renderer — no external libs */
function renderMarkdown(md: string): string {
  if (!md) return '';
  let html = md
    // Code blocks (``` ... ```)
    .replace(/```(\w*)\n([\s\S]*?)```/g, (_m, lang, code) =>
      `<pre style="background:${T.bgCard};padding:16px;border-radius:8px;overflow-x:auto;font-size:13px;line-height:1.5"><code class="lang-${lang}">${code.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</code></pre>`)
    // Inline code
    .replace(/`([^`]+)`/g, `<code style="background:${T.border};padding:2px 6px;border-radius:4px;font-size:13px">$1</code>`)
    // Headers
    .replace(/^### (.+)$/gm, `<h3 style="font-size:18px;font-weight:600;margin:24px 0 8px;color:${T.text}">$1</h3>`)
    .replace(/^## (.+)$/gm, `<h2 style="font-size:20px;font-weight:600;margin:28px 0 10px;color:${T.text}">$1</h2>`)
    .replace(/^# (.+)$/gm, `<h1 style="font-size:24px;font-weight:700;margin:32px 0 12px;color:${T.text}">$1</h1>`)
    // Bold & italic
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    // Links
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" style="color:#5B8DEF;text-decoration:none" target="_blank" rel="noopener">$1</a>')
    // Unordered lists
    .replace(/^- (.+)$/gm, '<li style="margin:4px 0;padding-left:4px">$1</li>')
    // Paragraphs (double newlines)
    .replace(/\n\n/g, '</p><p style="margin:12px 0">')
    // Single newlines within paragraph
    .replace(/\n/g, '<br/>');

  // Wrap loose <li> in <ul>
  html = html.replace(/((?:<li[^>]*>.*?<\/li>\s*)+)/g, '<ul style="margin:12px 0;padding-left:20px">$1</ul>');

  return `<p style="margin:12px 0">${html}</p>`;
}

export function ChangelogEntryPage(_props: { path?: string; locale?: string; slug?: string }) {
  const [entry, setEntry] = useState<Entry | null>(null);
  const [recent, setRecent] = useState<RecentEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const locale = _props.locale || 'pt-BR';
  const slug = _props.slug || '';

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    fetch(`${API_BASE}/entries/${encodeURIComponent(slug)}?contentType=changelog&locale=${encodeURIComponent(locale)}`)
      .then(r => r.json())
      .then((res: any) => {
        setEntry(res?.data?.entry || null);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [slug, locale]);

  useEffect(() => {
    fetch(`${API_BASE}/entries?contentType=changelog&status=published&locale=${encodeURIComponent(locale)}&limit=5`)
      .then(r => r.json())
      .then((res: any) => setRecent(res?.data?.entries || []))
      .catch(() => {});
  }, [locale]);

  return (
    <div style={S.page}>
      <div style={S.container}>
        <div style={S.main}>
          <Button variant="ghost" style={S.backLink} onClick={() => route(`/${locale}`)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            Voltar ao changelog
          </Button>

          {loading && <div style={S.skeleton} />}

          {!loading && !entry && (
            <div style={S.card}>
              <p style={{ color: T.textMuted, fontSize: 15 }}>Entrada nao encontrada.</p>
            </div>
          )}

          {!loading && entry && (
            <div style={S.card}>
              <h1 style={S.title}>{entry.title || entry.slug}</h1>
              <div style={S.meta}>
                <span style={S.date}>{formatDate(entry.published_at || entry.created_at)}</span>
                {(() => { try { const v = JSON.parse(entry.metadata || '{}').version; return v ? <span style={S.version}>v{v}</span> : null; } catch { return null; } })()}
                <div style={S.tags}>
                  {parseTags(entry.tags).map((tag) => (
                    <span key={tag} style={S.tag(TAG_COLORS[tag] || T.textMuted)}>{TAG_LABELS[tag] || tag}</span>
                  ))}
                </div>
              </div>
              <div style={S.content} dangerouslySetInnerHTML={{ __html: renderMarkdown(entry.body || '') }} />
            </div>
          )}
        </div>

        {/* Sidebar — recent entries */}
        <div style={S.sidebar}>
          <div style={S.sidebarTitle}>Recentes</div>
          {recent.length === 0 && (
            <>
              <div style={S.sidebarSkeleton} />
              <div style={S.sidebarSkeleton} />
              <div style={S.sidebarSkeleton} />
            </>
          )}
          {recent.map((r) => (
            <div
              key={r.id}
              style={{ ...S.recentCard, ...(r.slug === slug ? { borderColor: T.primaryBg } : {}) }}
              onClick={() => route(`/${locale}/${r.slug}`)}
              onMouseEnter={(e: any) => { e.currentTarget.style.borderColor = T.bgActive; }}
              onMouseLeave={(e: any) => { e.currentTarget.style.borderColor = r.slug === slug ? T.primaryBg : T.border; }}
            >
              <div style={S.recentTitle}>{r.title || r.slug}</div>
              <div style={S.recentDate}>{formatDate(r.published_at || r.created_at)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
